// Pestaña "Seguimiento paciente" (Fase 5 de "receta digital") — sección
// "Cita de control": agenda una cita SCHEDULED reusando el mismo motor de
// Agenda ya existente (crearCitaHce, Fase 4 de "destinos reales del nav").
// Cero backend nuevo a propósito — CONFIRMADO por investigación: cualquier
// cita SCHEDULED ya recibe confirmación + recordatorios 24h/1h automáticos
// (sweepCitas/citas-seguimiento.ts), sin importar el canal de creación. La
// especialidad se resuelve vía `obtenerMisEspecialidades()` (especialidades
// PROPIAS del practicante logueado, para el selector de "Nueva cita" de
// Agenda) — NUNCA `useEspecialidadDelPracticante` (esa es la del catálogo
// de formularios MSP/ACESS, un espacio de identificadores totalmente
// distinto; `crearCitaHce` exige un `specialtyId` de
// `tenant.config.specialties`).
import { formatHumanName } from '@medplum/core';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  cancelarSeguimientoHce,
  crearCitaHce,
  crearSeguimientoHce,
  listarSeguimientosHce,
  obtenerMisEspecialidades,
  reprogramarSeguimientoHce,
  type EspecialidadHce,
  type EstadoSeguimientoHce,
  type SeguimientoHce,
} from '@/lib/hceVault';
import type { PatientChartOutletContext } from './PatientChartPage';
import { obtenerPacienteVinculado } from './vaultClient';

type EstadoContacto =
  | { fase: 'cargando' }
  | { fase: 'sin-contacto' }
  | { fase: 'listo'; patientId: number; phone: string }
  | { fase: 'error'; mensaje: string };

type EstadoEnvio = { fase: 'idle' } | { fase: 'reservando' } | { fase: 'listo'; resumen: string } | { fase: 'error'; mensaje: string };

// Mismos labels que pages/admin/seguimiento-ia.tsx (CRM) — los colores se
// adaptan a los variants reales del Badge de este paquete (shadcn/hce-*,
// paleta distinta a los bg-indigo-brand/bg-amber-500/etc. del panel web),
// mismo criterio que VARIANTE_ESTADO en MedicamentosTab.tsx.
const ESTADO_VARIANTE: Record<EstadoSeguimientoHce, BadgeProps['variant']> = {
  scheduled: 'pine',
  sent: 'clay',
  responded: 'sage',
  cancelled: 'secondary',
  failed: 'destructive',
};

function SeguimientoIaSection({ patientId }: { readonly patientId: number }): JSX.Element {
  const { t } = useTranslation();
  const [seguimientos, setSeguimientos] = useState<SeguimientoHce[] | null>(null);
  const [cargando, setCargando] = useState(true);
  const [version, setVersion] = useState(0);
  const [noteBody, setNoteBody] = useState('');
  const [fechaHora, setFechaHora] = useState('');
  const [programando, setProgramando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reprogramandoId, setReprogramandoId] = useState<number | null>(null);
  const [reprogramarValor, setReprogramarValor] = useState('');
  const [cancelandoId, setCancelandoId] = useState<number | null>(null);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    listarSeguimientosHce(patientId)
      .then((r) => {
        if (!cancelado) setSeguimientos(r);
      })
      .catch(() => {
        if (!cancelado) setSeguimientos([]);
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [patientId, version]);

  function refrescar(): void {
    setVersion((v) => v + 1);
  }

  async function programar(): Promise<void> {
    if (!noteBody.trim() || !fechaHora) return;
    setProgramando(true);
    setError(null);
    try {
      await crearSeguimientoHce({ patientId, noteBody: noteBody.trim(), scheduledFor: new Date(fechaHora).toISOString() });
      setNoteBody('');
      setFechaHora('');
      refrescar();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
    setProgramando(false);
  }

  async function reprogramar(id: number): Promise<void> {
    if (!reprogramarValor) return;
    try {
      await reprogramarSeguimientoHce(id, new Date(reprogramarValor).toISOString());
      setReprogramandoId(null);
      setReprogramarValor('');
      refrescar();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function cancelar(id: number): Promise<void> {
    setCancelandoId(id);
    try {
      await cancelarSeguimientoHce(id);
      refrescar();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
    setCancelandoId(null);
  }

  const lista = seguimientos ?? [];

  return (
    <section className="flex flex-col gap-3 rounded-md border border-border p-3">
      <h6 className="text-sm font-medium">{t('seguimientoPaciente.iaTitulo')}</h6>
      <Alert>
        <AlertDescription>{t('seguimientoPaciente.iaAvisoPrivacidad')}</AlertDescription>
      </Alert>

      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-2">
        <Textarea value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder={t('seguimientoPaciente.iaNotaPlaceholder')} rows={3} />
        <Input type="datetime-local" value={fechaHora} onChange={(e) => setFechaHora(e.target.value)} />
        <Button type="button" size="sm" className="w-fit" disabled={!noteBody.trim() || !fechaHora || programando} onClick={() => void programar()}>
          {programando ? t('seguimientoPaciente.iaProgramando') : t('seguimientoPaciente.iaProgramar')}
        </Button>
      </div>

      {cargando ? (
        <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>
      ) : lista.length === 0 ? (
        <p className="text-sm text-hce-muted">{t('seguimientoPaciente.iaSinSeguimientos')}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {lista.map((s) => (
            <div key={s.id} className="rounded-md border border-border p-2.5">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-hce-ink whitespace-pre-wrap">{s.noteBody}</p>
                <Badge variant={ESTADO_VARIANTE[s.status]}>{t(`seguimientoPaciente.estado.${s.status}`)}</Badge>
              </div>
              <p className="mt-1 text-xs text-hce-muted">
                {s.status === 'sent' || s.status === 'responded'
                  ? `${t('seguimientoPaciente.iaEnviadoEl')} ${s.sentAt ? new Date(s.sentAt).toLocaleString('es-EC') : ''}`
                  : `${t('seguimientoPaciente.iaProgramadoPara')} ${new Date(s.scheduledFor).toLocaleString('es-EC')}`}
              </p>
              {s.status === 'failed' && s.lastError && <p className="text-xs text-destructive">{s.lastError}</p>}

              {s.status === 'scheduled' && reprogramandoId !== s.id && (
                <div className="mt-1.5 flex items-center gap-3 text-xs">
                  <button
                    type="button"
                    className="text-hce-pine hover:underline"
                    onClick={() => {
                      setReprogramandoId(s.id);
                      setReprogramarValor('');
                    }}
                  >
                    {t('seguimientoPaciente.iaReprogramar')}
                  </button>
                  <button
                    type="button"
                    className="text-hce-muted hover:text-destructive disabled:opacity-50"
                    disabled={cancelandoId === s.id}
                    onClick={() => void cancelar(s.id)}
                  >
                    {t('seguimientoPaciente.iaCancelar')}
                  </button>
                </div>
              )}
              {reprogramandoId === s.id && (
                <div className="mt-1.5 flex items-center gap-2">
                  <Input type="datetime-local" value={reprogramarValor} onChange={(e) => setReprogramarValor(e.target.value)} className="h-8 text-xs" />
                  <Button type="button" size="sm" disabled={!reprogramarValor} onClick={() => void reprogramar(s.id)}>
                    {t('seguimientoPaciente.iaGuardar')}
                  </Button>
                  <button type="button" className="text-xs text-hce-muted" onClick={() => setReprogramandoId(null)}>
                    {t('seguimientoPaciente.cancelar')}
                  </button>
                </div>
              )}

              {s.replies.map((r) => (
                <div key={r.id} className="mt-1.5 ml-1 border-l-2 border-hce-pine/30 pl-2 text-xs">
                  <p className="text-hce-ink whitespace-pre-wrap">{r.text}</p>
                  <p className="text-[10px] text-hce-muted">{new Date(r.repliedAt).toLocaleString('es-EC')}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function SeguimientoPacienteTab(): JSX.Element {
  const { t } = useTranslation();
  const outlet = useOutletContext<PatientChartOutletContext | undefined>();
  const patient = outlet?.patient;

  const [contacto, setContacto] = useState<EstadoContacto>({ fase: 'cargando' });
  const [especialidades, setEspecialidades] = useState<EspecialidadHce[] | null>(null);
  const [specialtyId, setSpecialtyId] = useState('');
  const [fechaHora, setFechaHora] = useState('');
  const [motivo, setMotivo] = useState('');
  const [modalidad, setModalidad] = useState<'presencial' | 'telemedicina'>('presencial');
  const [envio, setEnvio] = useState<EstadoEnvio>({ fase: 'idle' });

  useEffect(() => {
    if (!patient) return;
    let cancelado = false;
    setContacto({ fase: 'cargando' });
    const cedula = patient.identifier?.[0]?.value;
    if (!cedula) {
      setContacto({ fase: 'sin-contacto' });
      return;
    }
    obtenerPacienteVinculado(cedula)
      .then((vinculado) => {
        if (cancelado) return;
        if (!vinculado?.phone) {
          setContacto({ fase: 'sin-contacto' });
          return;
        }
        setContacto({ fase: 'listo', patientId: vinculado.patientId, phone: vinculado.phone });
      })
      .catch((err: unknown) => {
        if (!cancelado) setContacto({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelado = true;
    };
  }, [patient]);

  useEffect(() => {
    obtenerMisEspecialidades()
      .then((r) => {
        setEspecialidades(r);
        if (r.length === 1) setSpecialtyId(r[0]!.id);
      })
      .catch(() => setEspecialidades([]));
  }, []);

  async function agendar(): Promise<void> {
    if (contacto.fase !== 'listo' || !specialtyId || !fechaHora) return;
    setEnvio({ fase: 'reservando' });
    const nombre = patient?.name?.[0] ? formatHumanName(patient.name[0]) : (patient?.id ?? '');
    const resultado = await crearCitaHce({
      patientId: contacto.patientId,
      contactName: nombre,
      phone: contacto.phone,
      startsAtIso: new Date(fechaHora).toISOString(),
      specialtyId,
      notes: motivo.trim() || undefined,
      modalidad,
    });
    if (!resultado.ok) {
      setEnvio({ fase: 'error', mensaje: resultado.detail });
      return;
    }
    const especialidadElegida = especialidades?.find((e) => e.id === specialtyId);
    const modalidadLabel = t(`dashboard.modalidad.${modalidad}`);
    setEnvio({ fase: 'listo', resumen: `${especialidadElegida?.name ?? ''} — ${modalidadLabel} — ${new Date(fechaHora).toLocaleString('es-EC')}` });
    setFechaHora('');
    setMotivo('');
    setModalidad('presencial');
  }

  return (
    <div className="flex flex-col gap-4">
      <h5 className="text-sm font-semibold">{t('fichaPaciente.tabs.seguimiento')}</h5>

      <section className="flex flex-col gap-3 rounded-md border border-border p-3">
        <h6 className="text-sm font-medium">{t('seguimientoPaciente.citaControlTitulo')}</h6>
        <p className="text-xs text-hce-muted">{t('seguimientoPaciente.citaControlDescripcion')}</p>

        {contacto.fase === 'cargando' && <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>}
        {contacto.fase === 'sin-contacto' && (
          <Alert>
            <AlertDescription>{t('seguimientoPaciente.sinContacto')}</AlertDescription>
          </Alert>
        )}
        {contacto.fase === 'error' && (
          <Alert variant="red">
            <AlertDescription>{contacto.mensaje}</AlertDescription>
          </Alert>
        )}

        {contacto.fase === 'listo' && (
          <>
            {envio.fase === 'error' && (
              <Alert variant="red">
                <AlertDescription>{envio.mensaje}</AlertDescription>
              </Alert>
            )}
            {envio.fase === 'listo' ? (
              <div className="flex flex-col gap-2">
                <Alert>
                  <AlertDescription>
                    {t('seguimientoPaciente.citaAgendada')} {envio.resumen}
                  </AlertDescription>
                </Alert>
                <Button type="button" size="sm" variant="outline" className="w-fit" onClick={() => setEnvio({ fase: 'idle' })}>
                  {t('seguimientoPaciente.agendarOtra')}
                </Button>
              </div>
            ) : (
              <>
                <Select value={specialtyId} onValueChange={setSpecialtyId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('agenda.especialidad')} />
                  </SelectTrigger>
                  <SelectContent>
                    {(especialidades ?? []).map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {especialidades?.length === 0 && <p className="text-xs text-destructive">{t('agenda.sinEspecialidad')}</p>}

                <Select value={modalidad} onValueChange={(v) => setModalidad(v as 'presencial' | 'telemedicina')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="presencial">{t('dashboard.modalidad.presencial')}</SelectItem>
                    <SelectItem value="telemedicina">{t('dashboard.modalidad.telemedicina')}</SelectItem>
                  </SelectContent>
                </Select>

                <Input type="datetime-local" value={fechaHora} onChange={(e) => setFechaHora(e.target.value)} />
                <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder={t('seguimientoPaciente.motivoPlaceholder')} rows={2} />

                <Button
                  type="button"
                  size="sm"
                  className="w-fit"
                  disabled={!specialtyId || !fechaHora || envio.fase === 'reservando'}
                  onClick={() => void agendar()}
                >
                  {envio.fase === 'reservando' ? t('agenda.reservando') : t('seguimientoPaciente.agendar')}
                </Button>
              </>
            )}
          </>
        )}
      </section>

      {contacto.fase === 'listo' && <SeguimientoIaSection patientId={contacto.patientId} />}
    </div>
  );
}
